import { EventEmitter, Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable, takeUntil, timer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TimerService {

  private _timers: Map<string, AppTimer> = new Map();

  constructor() { }

  public registerTimer(name: string, initialTimerValueInSeconds: number) {
    if (this._timers.get(name)) {
      throw new Error(name + " timer is already registered");
    }
    const appTimer = this._createTimer(name, initialTimerValueInSeconds);
    this._resumeTimer(name, appTimer);
  }

  public startTimer(name: string, initialTimerValueInSeconds: number, eventEmitter?: EventEmitter<void>) {
    const timer = this._timers.get(name);
    if (timer.isTimerInUse$.value) {
      return;
    } else {
      if (eventEmitter) {
        eventEmitter.emit();
        if (initialTimerValueInSeconds === 0) {
          return;
        }
      }
      timer.isTimerInUse$.next(true);
      timer.timer$
      .subscribe(() => {
        const timerInSeconds = timer.timerInSeconds$.value;
        timer.timerInSeconds$.next(timerInSeconds - 1);
        if (timerInSeconds === 1) {
          sessionStorage.removeItem(name);
          timer.terminateTimer$.next(true);
          timer.isTimerInUse$.next(false);
          timer.timerInSeconds$.next(initialTimerValueInSeconds);
          this._createTimer(name, initialTimerValueInSeconds);
        } else if ((timerInSeconds % 5) === 0) {
          sessionStorage.setItem(name, timer.timerInSeconds$.value.toString());
        }
      });
    }
  }

  public timeLeft$(name: string): Observable<number> {
    return this._timers.get(name).timerInSeconds$.asObservable();
  }

  public isInUse$(name: string): Observable<boolean> {
    return this._timers.get(name).isTimerInUse$.asObservable();
  }

  private _createTimer(name: string, initialTimerValueInSeconds: number) {
    const isTimerInUse$ = new BehaviorSubject<boolean>(false);
    const terminateTimer$ = new BehaviorSubject<boolean>(false);

    const appTimer: AppTimer =  {
      name: name,
      isTimerInUse$: isTimerInUse$,
      terminateTimer$: terminateTimer$,
      timer$: timer(0, 1000).pipe(takeUntil(terminateTimer$.pipe(filter(x => x)))),
      timerInSeconds$: new BehaviorSubject(initialTimerValueInSeconds),
      initialTimerValueInSeconds: initialTimerValueInSeconds
    };
    this._timers.set(name, appTimer);

    return appTimer;
  }

  private _resumeTimer(name: string, appTimer: AppTimer) {
    const storedTime = sessionStorage.getItem(name);
    if (storedTime) {
      appTimer.timerInSeconds$.next(parseInt(storedTime));
      this.startTimer(name, appTimer.initialTimerValueInSeconds);
    }
  }
}

export interface AppTimer {
  name: string;
  isTimerInUse$: BehaviorSubject<boolean>;
  terminateTimer$: BehaviorSubject<boolean>;
  timer$: Observable<number>;
  timerInSeconds$: BehaviorSubject<number>;
  initialTimerValueInSeconds: number;
}
